import React, { useEffect, useRef, useState } from 'react'
import { useOfflinePack } from '../hooks/useOfflinePack'
import { OfflineScopeSheet } from './OfflineScopeSheet'
import { PAID_TIERS_ENABLED } from '../lib/payments'

interface Props {
  offlinePacksEnabled: boolean
  packQuestionsLimit: number
  subjectCodes: string[]
  /** Omit on the profile page so the card stays permanently available there. */
  onDismiss?: () => void
  /** Fires once when the first successful download finishes. */
  onDownloaded?: () => void
  /** Optional daily-credit context for the cost line. */
  dailyCreditsLimit?: number
  /** When below the pack cost, the download CTA disables. */
  dailyCreditsRemaining?: number
}

export function OfflinePackCard({
  offlinePacksEnabled,
  packQuestionsLimit,
  subjectCodes,
  onDismiss,
  onDownloaded,
  dailyCreditsLimit,
  dailyCreditsRemaining,
}: Props) {
  const { packMeta, hasPack, packAgeHours, packAgeText, downloading, error, downloadPack } = useOfflinePack()
  const [showScope, setShowScope] = useState(false)
  const unlimitedCredits = dailyCreditsLimit === -1 || dailyCreditsRemaining === -1
  const hasEnoughCredits =
    dailyCreditsRemaining == null || unlimitedCredits || dailyCreditsRemaining >= packQuestionsLimit
  const missingCredits =
    dailyCreditsRemaining != null && !unlimitedCredits
      ? Math.max(0, packQuestionsLimit - dailyCreditsRemaining)
      : 0
  const costLine =
    dailyCreditsLimit != null && dailyCreditsLimit > 0 && !unlimitedCredits
      ? `Usa ${packQuestionsLimit} dos teus ${dailyCreditsLimit} créditos de hoje. Depois praticas offline sem gastar mais.`
      : `Usa ${packQuestionsLimit} créditos do teu limite diário. Depois praticas offline sem gastar mais.`

  // Fire only on fresh downloads, not on first render with an existing pack.
  const prevHasPack = useRef(hasPack)
  useEffect(() => {
    if (!prevHasPack.current && hasPack && onDownloaded) onDownloaded()
    prevHasPack.current = hasPack
  }, [hasPack, onDownloaded])

  // Show the locked state only on surfaces that explicitly include this card.
  if (!offlinePacksEnabled || packQuestionsLimit <= 0) {
    return (
      <div className="mb-5 rounded-xl border border-gray-200 bg-gray-50 p-4 opacity-60">
        <h3 className="text-sm font-semibold text-gray-500 mb-1">Modo offline</h3>
        <p className="text-xs text-gray-400">
          {/* target=_blank to match openPricing(): same new-tab behaviour on
              the web, same hand-off to the real browser inside the PWA. The
              link is dropped entirely while payments are off, rather than
              pointing at the "em breve" placeholder. */}
          Disponível nos planos pagos{PAID_TIERS_ENABLED && (
            <> - <a href="/pricing" target="_blank" rel="noopener noreferrer" className="text-indigo-500 underline">ver planos</a></>
          )}
        </p>
      </div>
    )
  }

  if (hasPack && !downloading) {
    const isStale = packAgeHours > 72
    return (
      <>
        <div className={`mb-5 rounded-xl border p-4 ${isStale ? 'border-amber-200 bg-amber-50' : 'border-green-200 bg-green-50'}`}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="text-lg">{isStale ? '⚠️' : '✅'}</span>
              <h3 className="text-sm font-semibold text-gray-800">Questões offline</h3>
            </div>
            <span className="text-xs text-gray-400">{packAgeText}</span>
          </div>

          <div className="flex flex-wrap gap-1.5 mb-2">
            {packMeta!.subjects.map(s => (
              <span key={s.code} className="text-xs bg-white/80 border border-gray-200 rounded-full px-2 py-0.5 font-medium text-gray-600">
                {s.code}: {s.count}
              </span>
            ))}
            <span className="text-xs text-gray-400 self-center ml-1">
              {packMeta!.totalQuestions} questões
            </span>
          </div>

          {isStale && (
            <p className="text-xs text-amber-600 mb-2">Questões antigas - actualiza para versões novas</p>
          )}

          {error && <p className="text-xs text-red-500 mb-2">{error}</p>}

          <div className="flex items-center justify-between gap-3 mb-3">
            <p className="text-[11px] text-gray-500">
              Funciona offline e sincroniza depois.
            </p>
            <button
              onClick={() => setShowScope(true)}
              className="shrink-0 text-[11px] font-medium text-gray-500 underline hover:text-gray-700"
            >
              Saber mais
            </button>
          </div>

          <button
            onClick={downloadPack}
            className="w-full text-xs font-semibold py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition-colors"
          >
            🔄 Actualizar agora
          </button>
        </div>
        <OfflineScopeSheet open={showScope} onClose={() => setShowScope(false)} />
      </>
    )
  }

  if (downloading) {
    return (
      <div className="mb-5 rounded-xl border border-indigo-200 bg-indigo-50 p-4">
        <div className="flex items-center gap-2">
          <div className="h-4 w-4 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm font-semibold text-indigo-700">A guardar questões offline…</span>
        </div>
        <p className="text-xs text-indigo-400 mt-1">
          {subjectCodes.length} disciplina{subjectCodes.length > 1 ? 's' : ''} · até {packQuestionsLimit} questões
        </p>
      </div>
    )
  }

  return (
    <>
      <div className="mb-5 rounded-xl border border-indigo-200 bg-gradient-to-br from-indigo-50 to-blue-50 p-4 relative">
        {onDismiss && (
          <button
            onClick={onDismiss}
            aria-label="Dispensar"
            title="Dispensar"
            className="absolute top-2 right-2 text-indigo-300 hover:text-indigo-600 text-xl leading-none w-6 h-6 flex items-center justify-center"
          >
            ×
          </button>
        )}
        <div className="flex items-center gap-2 mb-1 pr-6">
          <span className="text-lg">📦</span>
          <h3 className="text-sm font-semibold text-gray-800">Questões offline</h3>
        </div>
        <p className="text-xs text-gray-500 mb-3">
          Descarrega para praticar sem internet.
        </p>

        <div className="flex flex-wrap gap-1.5 mb-3">
          {subjectCodes.map(code => (
            <span key={code} className="text-xs bg-white/80 border border-indigo-200 rounded-full px-2 py-0.5 font-medium text-indigo-600">
              {code}
            </span>
          ))}
        </div>

        {error && <p className="text-xs text-red-500 mb-2">{error}</p>}

        <button
          onClick={downloadPack}
          disabled={!hasEnoughCredits}
          className={`w-full text-sm font-semibold py-3 rounded-xl transition-colors ${
            hasEnoughCredits
              ? 'bg-indigo-600 text-white hover:bg-indigo-700'
              : 'bg-gray-200 text-gray-500 cursor-not-allowed'
          }`}
        >
          {hasEnoughCredits
            ? `⬇️ Guardar ${packQuestionsLimit} questões offline`
            : 'Créditos insuficientes'}
        </button>
        {!hasEnoughCredits && (
          <p className="text-xs text-amber-700 mt-2">
            Faltam {missingCredits} crédito{missingCredits === 1 ? '' : 's'}. Volta amanhã ou pratica online por agora.
          </p>
        )}
        <div className="flex items-center justify-between gap-3 mt-2">
          <p className="text-xs text-gray-400">
            {costLine}
          </p>
          <div className="flex items-center gap-3 shrink-0">
            <button
              onClick={() => setShowScope(true)}
              className="text-xs text-gray-400 underline hover:text-gray-600"
            >
              Saber mais
            </button>
            {onDismiss && (
              <button
                onClick={onDismiss}
                className="text-xs text-gray-400 hover:text-gray-600"
              >
                Não agora
              </button>
            )}
          </div>
        </div>
      </div>
      <OfflineScopeSheet open={showScope} onClose={() => setShowScope(false)} />
    </>
  )
}
